// @ts-strict-ignore
import { booleanRuleStrings } from '../../../constants';
import type {
  Rules,
  ObjectIdReverseTransformationMap,
  IndexedRule,
  BooleanRuleArray,
  AttributeComponent,
} from '../../../types';
import { getOperatorUsedInRuleObject } from '../../../update/helpers';

function operatorStringToOperator(operator: string) {
  if (operator === booleanRuleStrings.AND_OPERATOR_STRING) {
    return booleanRuleStrings.AND_OPERATOR;
  }
  if (operator === booleanRuleStrings.OR_OPERATOR_STRING) {
    return booleanRuleStrings.OR_OPERATOR;
  }

  return '';
}

function reverseTransformRuleArrayItems({
  ruleItem,
  transformedThresholdsToLegacyThresholdsIdMap,
}: {
  ruleItem: IndexedRule | BooleanRuleArray;
  transformedThresholdsToLegacyThresholdsIdMap: ObjectIdReverseTransformationMap;
}) {
  return (ruleItem as BooleanRuleArray)
    .map(rule => {
      if (rule[booleanRuleStrings.AND_OPERATOR_STRING]
        || rule[booleanRuleStrings.OR_OPERATOR_STRING]) {
        const ruleOperatorKey = rule[booleanRuleStrings.AND_OPERATOR_STRING]
          ? booleanRuleStrings.AND_OPERATOR_STRING
          : booleanRuleStrings.OR_OPERATOR_STRING;
        const transformedValue: (string | number)[] = reverseTransformRuleArrayItems({
          ruleItem: rule[ruleOperatorKey],
          transformedThresholdsToLegacyThresholdsIdMap,
        });

        const joinOperatorString: string = operatorStringToOperator(ruleOperatorKey);
        if (rule?.ambiguous) {
          return transformedValue.join(joinOperatorString);
        }
        return `(${transformedValue.join(joinOperatorString)})`;
      }

      if (rule.id) {
        const thresholdId = (rule as IndexedRule).id;
        return transformedThresholdsToLegacyThresholdsIdMap[thresholdId];
      }

      return 0;
    });
}

export default function reverseTransformBooleanRules({
  booleanRules,
  transformedThresholdsToLegacyThresholdsIdMap,
}: {
  booleanRules: Rules | {};
  transformedThresholdsToLegacyThresholdsIdMap: ObjectIdReverseTransformationMap;
}) {
  let booleanRuleAsString = '';
  const allOperatorsUsedTopLevel = [];

  const rules = Object.entries(booleanRules);
  rules.forEach(([ruleKey, ruleValue], ruleIndex) => {
    if (ruleKey === 'id' && ruleIndex === 0) {
      // this means it's 1 flat indexed rule
      booleanRuleAsString = `${transformedThresholdsToLegacyThresholdsIdMap[ruleValue]}`;
    }

    if (ruleKey === booleanRuleStrings.AND_OPERATOR_STRING
      || ruleKey === booleanRuleStrings.OR_OPERATOR_STRING) {
      // this means the rule has more than one threshold
      // and this is an array
      const transformedValue: (string | number)[] = reverseTransformRuleArrayItems({
        ruleItem: ruleValue,
        transformedThresholdsToLegacyThresholdsIdMap,
      });

      transformedValue.forEach((item, index) => {
        if (ruleValue[index]?.ambiguous) {
          const operator = getOperatorUsedInRuleObject({ rule: ruleValue[index] });
          if (operator) {
            const boolRuleStringPrefix = `${booleanRuleAsString}${operatorStringToOperator(operator)}`;
            if (booleanRuleAsString.length === 0) {
              booleanRuleAsString = `${booleanRuleAsString}${item}`;
              allOperatorsUsedTopLevel.push(operator);
            } else if (
              typeof item === 'string'
              && !item.startsWith('(')
              && !item.endsWith(')')
              && (item.includes('&') || item.includes('|'))
            ) {
              // this is for checking if the rule is like this (1|2&3&4|5)
              // when it's like this, technically "2&3&4" comes as one `item` in this block because they are formed as a group but without parenthesis
              // due to its ambiguous nature, so we do need to append operator inverted...
              // the last condition is to check if it has at least one & or | operator in it, not like "5"
              if (!getOperatorUsedInRuleObject({ rule: ruleValue[index - 1] })
                && ruleKey !== operator
                && ruleValue[index].ambiguousGroup
              ) {
                booleanRuleAsString = `${boolRuleStringPrefix}(${item})`;
              } else {
                const flippedOperator = operator === 'and' ? 'or' : 'and';
                booleanRuleAsString = `${booleanRuleAsString}${operatorStringToOperator(flippedOperator)}${item}`;
                allOperatorsUsedTopLevel.push(flippedOperator);
              }
            } else {
              booleanRuleAsString = `${booleanRuleAsString}${operatorStringToOperator(ruleKey)}${item}`;
              allOperatorsUsedTopLevel.push(operator);
            }
          }
        } else if (booleanRuleAsString.length === 0) {
          booleanRuleAsString = `${booleanRuleAsString}${item}`;
        } else {
          booleanRuleAsString = `${booleanRuleAsString}${operatorStringToOperator(ruleKey)}${item}`;
          allOperatorsUsedTopLevel.push(ruleKey);
        }
      });
    }
  });

  return `(${booleanRuleAsString})`;
}

export function reverseTransformMLAAttributeComponents({
  attributeFilterComponents,
  parentType,
}: {
  attributeFilterComponents: AttributeComponent[];
  parentType: 'threshold_left' | 'threshold_right' | 'dataRoute';
}) {
  return attributeFilterComponents.map(filterComp => {
    const commonAttributeComponents = {
      attribute_component_id: filterComp.attributeComponent.attributeComponentId,
      precedence: filterComp.attributeComponent.precedence,
      placeholder: filterComp.attributeComponent.placeholder,
      data_type: filterComp.attributeComponent.dataType,
      label: filterComp.attributeComponent.label,
      is_optional: filterComp.attributeComponent.isOptional,
      possible_values: filterComp.attributeComponent.possibleValues,
      default: filterComp.attributeComponent.default,
    };

    if (filterComp.value === undefined) return undefined; // value can be 0 if number type.
    const componentData = {
      value: filterComp.value,
    };

    if (parentType === 'threshold_left' || parentType === 'threshold_right') {
      const direction = parentType === 'threshold_left'
        ? 'left'
        : 'right';
      return {
        ...componentData,
        ...commonAttributeComponents,
        action_threshold_attribute_direction: direction,
      };
    }

    return {
      ...componentData,
      attribute_component: commonAttributeComponents,
      archived: false,
    };
  }).filter(filterComp => (filterComp));
}
